import {
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  useReactTable
} from '@tanstack/react-table';
import { differenceInYears, format, isValid } from 'date-fns';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import CalendarModal from '@/components/common/modals/calendar-modal/CalendarModal';
import StudentStatusEmojis from '@/components/common/studentStatusEmojis/StudentStatusEmojis';
import PresentCheckbox from '@/components/common/tables/students-table/PresentCheckbox';
import {
  exportAttendanceToExcel,
  exportDataOfStudentsToExcel
} from '@/components/common/tables/students-table/exportToExcel';
import type { ReservationInterface } from '@/components/common/tables/tables.types';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import useGetPaginatedReservations from '@/hooks/queries/reservations-api/useGetPaginatedReservation';
import useGetReservationsByServiceId from '@/hooks/queries/reservations-api/useGetReservationByServiceId';
import useCalendarPaginateNavigation from '@/hooks/useCalendarPaginateNavigation';
import usePersistedPaginationFiltersAndSorting from '@/hooks/usePersistedPaginationFiltersAndSorting';
import { getGroup, getGroupCampus, VEPCodes } from '@/lib/utils';
import type { ColumnDef, Row } from '@tanstack/react-table';
import useGetServiceWithDivisionRange from '../queries/services-api/useGetServiceWithDivisionRange';
import useUpdateUser from '../queries/users-api/useUptadeUser';
import useOrganizationName from '../useOrganizationName';

const useStudentsTable = () => {
  const { t } = useTranslation();
  const organizationName = useOrganizationName();
  const { data: services } = useGetServiceWithDivisionRange();
  const selectedServiceIdRef = useRef<string | null>(null); // Persisted reference
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
    null
  );
  const { mutate, isPending: isSending } = useUpdateUser();
  const [activeFetchingId, setActiveFetchingId] = useState('');

  const [openAlertModal, setOpenAlertModal] = useState<{
    id: string;
    state: boolean;
  }>({ id: '', state: false });

  // Group options for Select
  const groupOptions = VEPCodes.includes(organizationName)
    ? [
        { value: 'group-4-5', label: '4-5' },
        { value: 'group-6-7', label: '6-7' },
        { value: 'group-8-9', label: '8-9' },
        { value: 'group-+10', label: '+10' },
        { value: 'group-duplicate', label: 'Duplicado' }
      ]
    : [
        { value: 'group-a', label: 'A' },
        { value: 'group-b', label: 'B' },
        { value: 'group-c', label: 'C' }
      ];

  useEffect(() => {
    if (services && services.length > 0 && !selectedServiceIdRef.current) {
      const firstEnabledService = services.find((service) => service.enabled);
      if (firstEnabledService) {
        selectedServiceIdRef.current = firstEnabledService.id;
        setSelectedServiceId(firstEnabledService.id);
      }
    }
  }, [services]);

  const handleServiceSelection = (serviceId: string | null) => {
    selectedServiceIdRef.current = serviceId;
    setSelectedServiceId(serviceId);
  };

  const {
    pagination,
    setPagination,
    filtering,
    setFiltering,
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters
  } = usePersistedPaginationFiltersAndSorting({
    defaultSorting: [{ id: 'startTime', desc: false }]
  });

  const { date, setDay } = useCalendarPaginateNavigation(new Date());
  const TIMESTAMP_INIT = date
    ? new Date(date.setHours(0, 0, 0, 0)).getTime()
    : new Date();
  const TIMESTAMP_END = date
    ? new Date(date.setHours(23, 59, 59, 999)).getTime()
    : new Date();
  const {
    data: attendance,
    isLoading,
    isError,
    isFetching
  } = useGetPaginatedReservations({
    pagination,
    filtering,
    sorting,
    query: '&showConsumerConcurrent=true',
    columnFilters: [
      ...columnFilters,
      { id: 'sale.service.id', value: selectedServiceIdRef.current }, // Use ref here
      { id: 'startTime', value: { from: TIMESTAMP_INIT, to: TIMESTAMP_END } }
    ]
  });

  const [rowSelection, setRowSelection] = useState({});
  const reservationsData = attendance?.results;
  const renderExportButtons = VEPCodes.includes(organizationName);

  const { data: allReservations, isLoading: isLoadingAllReservations } =
    useGetReservationsByServiceId({
      serviceId: selectedServiceId ?? '',
      enabled: renderExportButtons,
      startDate: new Date(2026, 0, 1), //TODO eliminar hardcodeo
      endDate: new Date(2026, 12, 31) //TODO eliminar hardcodeo
    });

  const handleExport = async () => {
    try {
      exportDataOfStudentsToExcel(allReservations);
    } catch {
      toast.error(t('toast.downloadError'));
    }
  };

  const handleExportAttendance = async () => {
    try {
      exportAttendanceToExcel(allReservations as ReservationInterface[]);
    } catch {
      toast.error(t('toast.downloadError'));
    }
  };

  const getTodayDateFormatted = (date: Date | undefined): string => {
    if (!date || !isValid(date)) {
      return 'Fecha no válida';
    }
    return format(date, 'dd/MM');
  };

  const onChangeGroupCampus = (value: string, consumerId: string) => {
    setActiveFetchingId(consumerId);
    mutate({
      id: consumerId,
      body: {
        group: value
      }
    });
  };

  const columns = useMemo<ColumnDef<ReservationInterface>[]>(
    () => [
      ...(VEPCodes.includes(organizationName)
        ? [
            {
              id: 'state',
              accessorKey: 'state',
              header: t('activity.table.state'),
              enableSorting: false,
              enableColumnFilter: false,
              cell: ({ row }: { row: Row<ReservationInterface> }) => {
                const studentData = row.original.consumers[0];
                return <StudentStatusEmojis studentData={studentData} />;
              }
            }
          ]
        : []),
      {
        id: 'consumers.firstName',
        accessorKey: 'consumers.firstName',
        enableSorting: false,
        cell: ({ row }) => {
          const saleId = row.original.sale.id;
          const reservation = row.original;
          const status = reservation.reservationStatus.name;
          const studentName = `${row.original.consumers[0]?.firstName} ${row.original.consumers[0]?.lastName}`;
          const isRequested = status === 'Requested';
          const elementNotPay = (
            <Button
              variant="ghost"
              className="p-2"
              onClick={() => setOpenAlertModal({ id: saleId, state: true })}
            >
              <AlertCircle className="text-amber-600 shrink-0" />
            </Button>
          );
          const notPay = isRequested ? elementNotPay : null;
          return row.original.consumers ? (
            <div className="flex items-center gap-1">
              {studentName} {notPay}
            </div>
          ) : undefined;
        },
        header: t('activity.table.student')
      },
      {
        id: 'reservationStatus.name',
        accessorKey: 'reservationStatus.name',
        meta: { headerText: t('activity.table.attend') },
        enableSorting: false,
        header: () => (
          <div className="flex items-center gap-2">
            <span>{`${t('activity.table.attend')} (${getTodayDateFormatted(date)})`}</span>
            <CalendarModal
              placeholder=""
              value={undefined}
              disabled={date}
              defaultMonth={date}
              onAccept={(selectedDate) => {
                if (selectedDate) setDay(selectedDate);
              }}
            />
          </div>
        ),
        cell: ({ row }) => {
          const reservation = row.original;
          const status = reservation.reservationStatus.name;
          return (
            <PresentCheckbox
              reservationId={reservation.id}
              reservarionStatus={status}
            />
          );
        },
        enableColumnFilter: true
      },
      ...(VEPCodes.includes(organizationName)
        ? [
            {
              id: 'contact',
              accessorKey: 'contact',
              header: t('activity.table.contact'),
              enableSorting: false,
              accessorFn: (originalRow: ReservationInterface) => {
                const additionalData = originalRow.consumers[0]?.additionalData;
                return additionalData
                  ? `${additionalData?.parents[0].firstName} ${additionalData?.parents[0].lastName}`
                  : undefined;
              }
            }
          ]
        : []),
      {
        id: 'sale.saleConsumers.consumer.additionalData',
        accessorKey: 'sale.saleConsumers.consumer.additionalData',
        enableSorting: false,
        accessorFn: (originalRow: ReservationInterface) => {
          const additionalData = originalRow.consumers[0]?.additionalData;
          return VEPCodes.includes(organizationName)
            ? getGroup(additionalData)
            : getGroupCampus(additionalData);
        },
        header: t('activity.student.group'),
        cell: ({ row }: { row: Row<ReservationInterface> }) => {
          const consumer = row.original.consumers[0];
          const additionalData = consumer?.additionalData;
          const currentGroup = additionalData?.group;
          const currentGroupLabel = VEPCodes.includes(organizationName)
            ? getGroup(additionalData)
            : getGroupCampus(additionalData);

          return (
            <Select
              value={currentGroup || undefined}
              onValueChange={(value) => {
                onChangeGroupCampus(value, consumer.id);
              }}
              disabled={
                (isSending || isFetching) && activeFetchingId === consumer.id
              }
            >
              <SelectTrigger className="w-auto h-8">
                <SelectValue placeholder={currentGroupLabel || 'No'} />
              </SelectTrigger>
              <SelectContent>
                {groupOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
      },
      {
        id: 'sale.saleConsumers.consumer.birthdate',
        accessorKey: 'sale.saleConsumers.consumer.birthdate',
        accessorFn: (originalRow) => {
          return originalRow.consumers[0]?.birthdate
            ? differenceInYears(
                new Date(),
                new Date(originalRow.consumers[0]?.birthdate)
              )
            : undefined;
        },
        header: t('activity.student.age')
      },
      {
        id: 'obvervations',
        accessorKey: 'obvervations',
        enableSorting: false,
        header: t('activity.table.observation'),
        accessorFn: (originalRow) => {
          const additionalData = originalRow.consumers[0]?.additionalData;
          return additionalData ? additionalData?.observations : undefined;
        }
      },
      {
        id: 'actions',
        enableHiding: false,
        cell: ({ row }) =>
          row.original.consumers && (
            <Link
              to={`students/${row.original.consumers[0]?.id}`}
              className="w-full"
            >
              <ArrowRight />
            </Link>
          )
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date, t, groupOptions, organizationName]
  );

  const table = useReactTable({
    data: reservationsData ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    rowCount: attendance?.total,
    pageCount: Math.ceil((attendance?.total ?? 0) / (attendance?.limit ?? 1)),
    manualPagination: true,
    onSortingChange: setSorting,
    enableMultiSort: false,
    manualSorting: true,
    sortDescFirst: true,
    onColumnFiltersChange: setColumnFilters,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onRowSelectionChange: setRowSelection,
    initialState: {
      columnVisibility: {
        id: false
      }
    },
    state: {
      columnFilters,
      rowSelection,
      pagination,
      sorting
    }
  });

  return {
    table,
    services,
    organizationName,
    selectedServiceId: selectedServiceIdRef.current, // Return ref value
    attendanceDate: getTodayDateFormatted(date),
    setSelectedServiceId: handleServiceSelection, // Use handler
    isLoading,
    isError,
    isFetching,
    handleExport,
    handleExportAttendance,
    filtering,
    setFiltering,
    isLoadingAllReservations,
    allReservations,
    renderExportButtons,
    openAlertModal,
    setOpenAlertModal
  };
};

export default useStudentsTable;
